<template>
    <ion-page>
        <base-header title="Scadenze Contratti" :show-menu="true"></base-header>
        <ion-content :fullscreen="true">
            <ion-refresher slot="fixed" @ionRefresh="handleRefresh($event)">
                <ion-refresher-content></ion-refresher-content>
            </ion-refresher>
            <ion-header collapse="condense">
                <ion-toolbar>
                    <ion-title size="large">Scadenze Contratti</ion-title>
                </ion-toolbar>
            </ion-header>
            <ion-searchbar v-model="search" placeholder="Cerca cliente"></ion-searchbar>
            <ion-list v-if="!areCostumersLoading" class="contract-list-item">
                <ion-item
                    v-for="costumer in costumers"
                    :key="costumer.id"
                    class="contract-list-item ion-activatable ripple-parent"
                    @click="router.push({ name: 'costumerDetails', params: { costumerId: costumer.id } })">
                    <ion-label class="contract-list-item-label">
                        <ion-text>
                            <h2>
                                <b>{{ costumer.business_name }}</b>
                            </h2>
                        </ion-text>
                        <ion-note>
                            <h5>{{ costumer.city }}</h5>
                        </ion-note>
                    </ion-label>
                    <ion-ripple-effect></ion-ripple-effect>
                    <div slot="end">
                        <small v-if="costumer.latest_contract_expiration" :class="`expiration-badge ${getExpirationClass(costumer.latest_contract_expiration)}`">
                            {{ formatDate(costumer.latest_contract_expiration) }}
                        </small>
                        <ion-note v-else>
                            <em>Nessun contratto</em>
                        </ion-note>
                    </div>
                </ion-item>
                <ion-item v-if="costumers.length === 0 && search.length !== 0" class="ion-text-center" lines="none">
                    <ion-text color="medium" class="w-full">
                        <h3>Nessun cliente trovato</h3>
                        <p>Prova a modificare i criteri di ricerca.</p>
                    </ion-text>
                </ion-item>
                <ion-item v-if="costumers.length === 0 && search.length === 0" class="ion-text-center" lines="none">
                    <ion-text color="medium" class="w-full">
                        <h3>Nessun cliente con contratto</h3>
                    </ion-text>
                </ion-item>
            </ion-list>
            <ion-list v-else class="contract-list-item">
                <ion-item class="contract-list-item" v-for="index in [1, 2, 3, 4, 5, 6, 7, 8, 9]" :key="index">
                    <ion-label>
                        <h3>
                            <ion-skeleton-text :animated="true" style="width: 80%"></ion-skeleton-text>
                        </h3>
                        <p>
                            <ion-skeleton-text :animated="true" style="width: 60%"></ion-skeleton-text>
                        </p>
                    </ion-label>
                </ion-item>
            </ion-list>
        </ion-content>
    </ion-page>
</template>

<script setup lang="ts">
import {
    IonPage,
    IonSearchbar,
    IonHeader,
    IonToolbar,
    IonTitle,
    IonContent,
    IonList,
    IonText,
    IonItem,
    IonSkeletonText,
    IonNote,
    IonLabel,
    IonRippleEffect,
    IonRefresher,
    IonRefresherContent,
    toastController,
} from "@ionic/vue";
import { useCostumersStore } from "@/stores/costumers";
import { computed, ref } from "vue";
import router from "@/router";
import BaseHeader from "@/components/BaseHeader.vue";
import { format, parseISO, differenceInDays } from "date-fns";
import { it } from "date-fns/locale";
import * as Sentry from "@sentry/vue";

const costumersStore = useCostumersStore();

const search = ref<string>("");
const areCostumersLoading = computed(() => costumersStore.isLoading);

const costumers = computed(() =>
    costumersStore.getCostumers
        .filter(
            (costumer) =>
                costumer.business_name.toLowerCase().includes(search.value.toLowerCase()) ||
                costumer.city.toLowerCase().includes(search.value.toLowerCase())
        )
        .sort((a, b) => {
            if (!a.latest_contract_expiration && !b.latest_contract_expiration) return 0;
            if (!a.latest_contract_expiration) return 1;
            if (!b.latest_contract_expiration) return -1;
            return new Date(a.latest_contract_expiration).getTime() - new Date(b.latest_contract_expiration).getTime();
        })
);

const formatDate = (dateString: string): string => {
    try {
        return format(parseISO(dateString), "dd MMM yyyy", { locale: it });
    } catch {
        return dateString;
    }
};

const getExpirationClass = (dateString: string): string => {
    try {
        const expirationDate = parseISO(dateString);
        const today = new Date();
        const daysUntilExpiration = differenceInDays(expirationDate, today);

        if (daysUntilExpiration < 0) return "expired";
        if (daysUntilExpiration <= 30) return "expiring-soon";
        if (daysUntilExpiration <= 90) return "expiring";
        return "valid";
    } catch {
        return "";
    }
};

const handleRefresh = async (event: CustomEvent) => {
    try {
        await costumersStore.loadCostumers();
        event.detail.complete();
    } catch (error) {
        console.error("Errore durante l'aggiornamento della lista:", error);
        const toast = await toastController.create({
            message: "Errore durante l'aggiornamento della lista",
            duration: 2000,
            position: "bottom",
            color: "danger",
        });
        await toast.present();
        Sentry.captureException(error);
    }
};
</script>

<style lang="scss">
.contract-list-item {
    position: relative;
    --padding-start: 0;
    --padding-end: 0;
    --inner-padding-start: 16px;
    --inner-padding-end: 16px;
}

.contract-list-item-label h2 {
    font-size: 1.3rem;
    white-space: normal;
}

.expiration-badge {
    display: inline-block;
    padding: 4px 8px;
    font-weight: bold;
    border-radius: 4px;
    font-size: 0.85rem;

    &.valid {
        background-color: green;
        color: white;
    }

    &.expiring {
        background-color: yellow;
        color: var(--ion-background-color);
    }

    &.expiring-soon {
        background-color: orange;
        color: white;
    }

    &.expired {
        background-color: red;
        color: white;
    }
}
</style>
